import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { CreatePlan, UpdatePlan } from './SubscriptionSlice';

const PlanModal = ({ show, onClose, plan, isAdmin = true }) => {
  const dispatch = useDispatch();
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({
    plan_type: 'general', // 'general' or 'custom'
    company_id: '',
    plan_name: '',
    description: '',
    price: '',
    limit_type: 'limited', // 'limited' or 'unlimited'
    student_limit: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Safety check to ensure isAdmin is defined
  if (typeof isAdmin === 'undefined') {
    console.warn('isAdmin prop is not defined, defaulting to true');
    isAdmin = true;
  }

  useEffect(() => {
    // Get companies from localStorage (same as dashboard page)
    const getCompaniesFromStorage = () => {
      try {
        // Try different localStorage keys that might be used
        const possibleKeys = [
          'companies',
          'companyList',
          'companiesList',
          'adminCompanies',
          'userCompanies'
        ];
        
        let companiesData = [];
        
        for (const key of possibleKeys) {
          const stored = localStorage.getItem(key);
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              companiesData = parsed.data || parsed.companies || parsed || [];
              console.log(`Found companies in localStorage key "${key}":`, companiesData);
              console.log('Company structure sample:', companiesData[0]);
              
              // Ensure each company has proper id and name (use company_name like DashboardData)
              companiesData = companiesData.map(company => ({
                id: company.id || company.companyId || company._id,
                name: company.company_name || company.name || company.companyName || company.business_name || `Company ${company.id || 'Unknown'}`,
                company_name: company.company_name || company.name || company.companyName || company.business_name || `Company ${company.id || 'Unknown'}`,
                ...company
              }));
              
              console.log('Processed companies:', companiesData);
              break;
            } catch (parseError) {
              console.log(`Failed to parse ${key}:`, parseError);
            }
          }
        }
        
        // If still no companies found, try to get from Redux store or use mock data
        if (companiesData.length === 0) {
          console.warn('No companies found in localStorage, using mock data');
          companiesData = [
            { id: 1, name: 'Company 1' },
            { id: 2, name: 'Company 2' },
            { id: 3, name: 'Company 3' }
          ];
        }
        
        setCompanies(companiesData);
      } catch (error) {
        console.error('Error getting companies from localStorage:', error);
        // Set default companies as fallback
        setCompanies([
          { id: 1, name: 'Company 1' },
          { id: 2, name: 'Company 2' }
        ]);
      }
    };

    if (show) { // Only get companies when modal is open
      getCompaniesFromStorage();
    }

    if (plan) {
      setFormData({
        plan_type: plan.plan_type || 'general',
        company_id: plan.company_id || '',
        plan_name: plan.plan_name || '',
        description: plan.description || '',
        price: plan.price || '',
        limit_type: plan.student_limit === 0 ? 'unlimited' : 'limited',
        student_limit: plan.student_limit || '',
        is_active: plan.is_active !== undefined ? plan.is_active : true
      });
    } else {
      // Reset form for new plan
      setFormData(prev => ({
        ...prev,
        plan_type: 'general',
        company_id: '',
        plan_name: '',
        description: '',
        price: '',
        limit_type: 'limited',
        student_limit: '',
        is_active: true
      }));
    }
  }, [plan, show]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    
    // Plan type validation
    if (!formData.plan_type) {
      newErrors.plan_type = 'Plan type is required';
    }
    
    // Company validation for custom plans
    if (formData.plan_type === 'custom' && !formData.company_id) {
      newErrors.company_id = 'Company selection is required for custom plans';
    }
    
    // Plan name validation
    if (!formData.plan_name.trim()) {
      newErrors.plan_name = 'Plan name is required';
    }
    
    // Price validation
    if (!formData.price || parseFloat(formData.price) < 0) {
      newErrors.price = 'Valid price is required';
    }
    
    // Student limit validation
    if (formData.limit_type === 'limited' && (!formData.student_limit || parseInt(formData.student_limit) < 1)) {
      newErrors.student_limit = 'Student limit must be at least 1 for limited plans';
    }
    
    // For unlimited plans, set student_limit to 0
    if (formData.limit_type === 'unlimited') {
      setFormData(prev => ({ ...prev, student_limit: 0 }));
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      // Prepare data with proper student_limit value
      const submitData = {
        ...formData,
        student_limit: formData.limit_type === 'unlimited' ? 0 : formData.student_limit
      };

      const result = plan
        ? await dispatch(UpdatePlan({ id: plan.id, data: submitData }))
        : await dispatch(CreatePlan(submitData));

      if (result.payload?.code === 1) {
        toast.success(plan ? 'Plan updated successfully' : 'Plan created successfully');
        onClose(true);
      } else {
        toast.error(result.payload?.message || 'Operation failed');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={() => onClose(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{plan ? 'Edit Plan' : 'Create Plan'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Plan Type *</Form.Label>
            <div className="d-flex gap-3 mb-3">
              <Form.Check
                type="radio"
                name="plan_type"
                id="general-plan"
                label="General Plan"
                value="general"
                checked={formData.plan_type === 'general'}
                onChange={handleChange}
              />
              <Form.Check
                type="radio"
                name="plan_type"
                id="custom-plan"
                label="Custom Plan"
                value="custom"
                checked={formData.plan_type === 'custom'}
                onChange={handleChange}
              />
            </div>
          </Form.Group>

          {/* Company Selection - Only show for Custom Plans */}
          {formData.plan_type === 'custom' && (
            <Form.Group className="mb-3">
              <Form.Label>Company *</Form.Label>
              <Form.Select
                name="company_id"
                value={formData.company_id}
                onChange={handleChange}
                isInvalid={!!errors.company_id}
              >
                <option value="">Select Company</option>
                {companies.map(company => {
                  console.log('Rendering company:', company);
                  return (
                    <option key={company.id} value={company.id}>
                      {company.company_name || company.name || `Company ${company.id || 'Unknown'}`}
                    </option>
                  );
                })}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.company_id}
              </Form.Control.Feedback>
              
              {/* Debug info */}
              {companies.length === 0 && (
                <div className="text-muted small mt-2">
                  No companies found. Check localStorage for company data.
                </div>
              )}
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Plan Name *</Form.Label>
            <Form.Control
              type="text"
              name="plan_name"
              value={formData.plan_name}
              onChange={handleChange}
              isInvalid={!!errors.plan_name}
              placeholder="Enter plan name"
              disabled={formData.plan_type === 'general' && !isAdmin} // General plans can't be edited by non-admins
            />
            <Form.Control.Feedback type="invalid">
              {errors.plan_name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter plan description"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Price (£) *</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              name="price"
              value={formData.price}
              onChange={handleChange}
              isInvalid={!!errors.price}
              placeholder="0.00"
            />
            <Form.Control.Feedback type="invalid">
              {errors.price}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Student Limit *</Form.Label>
            <div className="d-flex gap-3 mb-2">
              <Form.Check
                type="radio"
                name="limit_type"
                id="limited-students"
                label="Limited"
                value="limited"
                checked={formData.limit_type === 'limited'}
                onChange={handleChange}
              />
              <Form.Check
                type="radio"
                name="limit_type"
                id="unlimited-students"
                label="Unlimited"
                value="unlimited"
                checked={formData.limit_type === 'unlimited'}
                onChange={handleChange}
              />
            </div>
            
            {/* Student Limit Input - Only show if Limited is selected */}
            {formData.limit_type === 'limited' && (
              <Form.Control
                type="number"
                name="student_limit"
                value={formData.student_limit}
                onChange={handleChange}
                isInvalid={!!errors.student_limit}
                placeholder="Enter student limit"
                min="1"
              />
            )}
            
            <Form.Control.Feedback type="invalid">
              {errors.student_limit}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              name="is_active"
              label="Active"
              checked={formData.is_active}
              onChange={handleChange}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onClose(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Saving...' : plan ? 'Update' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PlanModal;
